import { useReducer, useEffect } from 'react'
import api from '@api'
import { Order, IPageInfo, BMTableBuilding, IHRLocation, IBuildingType } from '@api/types'
import { uniqueId, flatten } from 'lodash'
import { useSelection } from '@hooks'
import { ISelection } from '@hooks/useSelection'
import { useShouldReload } from '@hooks/reloadContext'
import { IBuildingVariables } from '@api/building/list'
import { IGridTableCell, GridTableStatusEnum } from '@ui'

const PAGE_SIZE = 25
const MAX_PAGE_SIZE = 150

enum Actions {
  LOAD_START,
  LOAD_START_WITHOUT_SKELETON,
  LOAD,
  SORT,
  NEXT_PAGE,
  QUERY,
  ERROR,
  OPTIMISTIC_DELETE,
  TOGGLE_INCLUDE_OLD,
  RESET_FILTERS,
  FILTER,
}

export interface Filters {
  cities: any[]
  includeOld: boolean
  HRLocations: IHRLocation[]
  buildingTypes: IBuildingType[]
  query: string
}

interface LoadStartAction {
  type: Actions.LOAD_START
  reqId: string
}

interface LoadStartActionWithoutSkeleton {
  type: Actions.LOAD_START_WITHOUT_SKELETON
  reqId: string
}

interface LoadAction {
  type: Actions.LOAD
  reqId: string
  items: IGridTableCell<BMTableBuilding>[]
  count: number
  pageInfo: IPageInfo
}

interface SortAction {
  type: Actions.SORT
  column: string
}

interface NextPageAction {
  type: Actions.NEXT_PAGE
}

interface QueryAction {
  type: Actions.QUERY
  query: string
}

interface ErrorAction {
  type: Actions.ERROR
  reqId: string
}
interface OptimisticDeleteAction {
  type: Actions.OPTIMISTIC_DELETE
  ids: string[]
}

interface ToggleIncludeOldAction {
  type: Actions.TOGGLE_INCLUDE_OLD
}

interface ResetFiltersAction {
  type: Actions.RESET_FILTERS
}

interface ApplyFiltersAction {
  type: Actions.FILTER
  filters: Filters
}

export interface IDHash {
  [id: string]: boolean
}

type Action =
  | LoadAction
  | LoadStartAction
  | NextPageAction
  | SortAction
  | QueryAction
  | ErrorAction
  | LoadStartActionWithoutSkeleton
  | OptimisticDeleteAction
  | ToggleIncludeOldAction
  | ResetFiltersAction
  | ApplyFiltersAction

interface Sort {
  column: string
  order: Order
}

interface State {
  reqId: string
  status: GridTableStatusEnum
  count: number
  items: IGridTableCell<BMTableBuilding>[]
  after: string | null
  pageInfo: IPageInfo
  sort: Sort
  first: number
  filters: Filters
}

export interface UseBuildingsHook extends State {
  sortBy: (column: string) => void
  nextPage: () => void
  selection: ISelection
  optimisticDelete: (ids: string[]) => void
  resetFilters: () => void
  applyFilters: (filters: Filters) => void
}

let persistedState: State = {
  reqId: uniqueId('request'),
  status: GridTableStatusEnum.LOADING,
  count: 0,
  first: PAGE_SIZE,
  items: [],
  after: null,
  sort: { column: 'name', order: Order.ASC },
  pageInfo: { hasNextPage: false, endCursor: '' },
  filters: {
    includeOld: false,
    HRLocations: [],
    buildingTypes: [],
    cities: [],
    query: '',
  },
}

interface GetVariablesFunc {
  sort: Sort
  after?: string | null
  first?: number
  filters: Filters
}

export function getVariables({
  sort,
  after,
  first,
  filters,
}: GetVariablesFunc): IBuildingVariables {
  const filterList = []

  filterList.push({
    includeOldBuildings: filters.includeOld,
  })

  if (filters.cities.length) {
    const cities = flatten(
      filters.cities.map((regOrCity) => (regOrCity.cities ? regOrCity.cities : regOrCity))
    ).map(({ name }) => name)
    filterList.push({ city_in: cities })
  }

  if (filters.HRLocations.length) {
    filterList.push({
      geoLocationId_in: filters.HRLocations.map(({ id }) => id),
    })
  }

  if (filters.buildingTypes.length) {
    filterList.push({
      buildingTypeId_in: filters.buildingTypes.map(({ id }) => id),
    })
  }

  if (filters.query) {
    filterList.push({
      OR: [
        { name_contains: filters.query },
        { code_contains: filters.query },
        { address_contains: filters.query },
      ],
    })
  }

  const where = {
    AND: filterList,
  }

  return {
    after,
    orderBy: sort.column + '_' + sort.order,
    first,
    ...(where.AND.length
      ? {
          where,
        }
      : {}),
  }
}

export default function useBuildingsNew(): UseBuildingsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const selection = useSelection()
  const shouldReload = useShouldReload()

  const fetchBuildings =
    ({ showSkeleton, loadAll }: { showSkeleton: boolean; loadAll: boolean }) =>
    () => {
      const reqId = uniqueId('request')
      const variables = getVariables({
        after: loadAll ? null : state.after,
        sort: state.sort,
        first: loadAll ? state.first : PAGE_SIZE,
        filters: state.filters,
      })

      if (showSkeleton) {
        dispatch({ type: Actions.LOAD_START, reqId })
      } else {
        dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })
      }

      api.building.list(variables).then(({ edges, counters, pageInfo }) => {
        const items = edges.map(({ node }) => ({ id: node.id, data: node }))

        selection.removeSelectionsByItems(items)

        dispatch({
          type: Actions.LOAD,
          count: counters.total,
          items,
          pageInfo,
          reqId,
        })
      })
    }

  //api request without skeleton and with load only 25 items after cursor
  useEffect(fetchBuildings({ showSkeleton: false, loadAll: false }), [state.after])

  //api request without skeleton and with load all items in state.first
  useEffect(fetchBuildings({ showSkeleton: false, loadAll: true }), [
    state.sort,
    shouldReload.buildings,
  ])

  //api request with skeleton and with load all items in state.first
  useEffect(fetchBuildings({ showSkeleton: true, loadAll: true }), [
    state.filters.includeOld,
    state.filters.HRLocations,
    state.filters.query,
    state.filters.cities,
    state.filters.buildingTypes,
  ])

  useEffect(() => {
    persistedState = { ...state, after: null }
  })

  return {
    ...state,
    nextPage: () => {
      dispatch({ type: Actions.NEXT_PAGE })
    },
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    optimisticDelete: (ids: string[]) => {
      dispatch({ type: Actions.OPTIMISTIC_DELETE, ids })
    },
    selection: selection,
    resetFilters: () => {
      dispatch({
        type: Actions.RESET_FILTERS,
      })
    },
    applyFilters: (filters: Filters) => {
      dispatch({
        type: Actions.FILTER,
        filters,
      })
    },
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.LOADING,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.IDLE,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId ? { ...state, status: GridTableStatusEnum.IDLE } : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            status: GridTableStatusEnum.IDLE,
            count: action.count,
            items:
              state.status === GridTableStatusEnum.PAGING
                ? [...state.items, ...action.items]
                : action.items,
            pageInfo: action.pageInfo,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        status: GridTableStatusEnum.SORTING,
        pageInfo: { hasNextPage: false, endCursor: '' },
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }

    case Actions.NEXT_PAGE:
      return {
        ...state,
        after: state.pageInfo.endCursor,
        status: GridTableStatusEnum.PAGING,
        first: Math.min(MAX_PAGE_SIZE, state.first + PAGE_SIZE),
      }

    case Actions.OPTIMISTIC_DELETE:
      return {
        ...state,
        items: state.items.filter((building) => !action.ids.includes(building.data.id)),
      }

    case Actions.RESET_FILTERS:
      return {
        ...state,
        filters: {
          includeOld: false,
          HRLocations: [],
          buildingTypes: [],
          cities: [],
          query: '',
        },
      }

    case Actions.FILTER:
      return {
        ...state,
        filters: action.filters,
        pageInfo: { hasNextPage: false, endCursor: '' },
      }

    default:
      return state
  }
}
